import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { MessageModel, TTSStreamChunk } from '../types/models';
import { apiClient } from '../clients/APIClient';
import { useToastStore } from './ToastStore';
import { base64ToArrayBuffer } from '../utils/audio';
import { SSEStreamHandler } from '../utils/sseStreamHandler';
import {
    getSharedAudioContext,
    suspendSharedAudioContext,
    resumeSharedAudioContext,
    closeSharedAudioContext,
} from '../contexts/audioContext';

// Streaming Audio State (Web Audio API)
let audioContext: AudioContext | null = null;
let nextPlaybackStartTime = 0;
const scheduledSourceNodes = new Set<AudioBufferSourceNode>();
let hasStreamCompleted = false;
let isStreamActive = false;
let hasReceivedFirstChunk = false;
let paused = false;
const sseHandler = new SSEStreamHandler();

// Queued audio accumulated while paused; flushed on resume
const queuedAudioBuffers: AudioBuffer[] = [];
let queuedAudioDurationSec = 0;
const DEFAULT_MAX_PAUSE_BUFFER_SEC = 30;

// Caching audio 
const cachedAudioBuffers: AudioBuffer[] = [];
let hasCompleteAudioCached = false;
let cachedAudioMessage = '';

// --- Helper Functions ---
// Ensure a single AudioContext exists (shared)
const getOrCreateAudioContext = (): AudioContext => {
    if (!audioContext) {
        audioContext = getSharedAudioContext();
    }
    return audioContext;
};

interface TTSStore {
    // --- TTSContext States ---//
    // UI State
    isVisible: boolean;
    text: string;
    language: string;
    source: 'response' | 'text-selection' | null;
    isInTransition: boolean;

    // Player State
    isPlaying: boolean;
    isLoading: boolean;
    isPaused: boolean;
    hasActive: boolean;

    // Operations
    showTTSPlayer: (text: string, language?: string, source?: 'response' | 'text-selection') => void;
    hideTTSPlayer: () => void;
    
    play: (message: MessageModel, sanitize?: boolean, language?: string, source?: 'response' | 'text-selection') => Promise<void>;
    stop: (clearCache?: boolean) => void;
    pause: () => Promise<void>;
    resume: () => Promise<void>;
    replay: () => Promise<boolean>;
    
    hasCachedAudio: () => boolean;
    getCachedMessage: () => string;
    isActiveSource: (source: 'response' | 'text-selection') => boolean;
}

export const useTTSStore = create<TTSStore>()(
    devtools(
        (set, get) => {

    // Schedule an AudioBuffer for gapless playback
    const scheduleAudioBuffer = (audioBuffer: AudioBuffer) => {
        const ctx = getOrCreateAudioContext();
        const sourceNode = ctx.createBufferSource();
        sourceNode.buffer = audioBuffer;
        sourceNode.connect(ctx.destination);

        const startAt = Math.max(
            ctx.currentTime,
            nextPlaybackStartTime || ctx.currentTime,
        );
        try {
            sourceNode.start(startAt);
        } catch {
            sourceNode.start();
        }
        nextPlaybackStartTime = startAt + audioBuffer.duration;

        scheduledSourceNodes.add(sourceNode);
        sourceNode.onended = () => {
            try {
                sourceNode.disconnect();
            } catch (error) {
                console.debug('AudioNode disconnect failed during cleanup:', error);
            }
            scheduledSourceNodes.delete(sourceNode);
            
            setTimeout(() => {
                if (hasStreamCompleted && scheduledSourceNodes.size === 0 && !paused) {
                    // Playback finished naturally
                    cleanupStreamingAudio(false);
                    set({ isPlaying: false }, false, 'tts/playbackEnded');
                }
            }, 100);
        };
    };

    const enqueueBuffer = (audioBuffer: AudioBuffer) => {
        queuedAudioBuffers.push(audioBuffer);
        queuedAudioDurationSec += audioBuffer.duration;
        // Bound total buffered duration only when not paused
        if (!paused) {
            while (
                queuedAudioDurationSec > DEFAULT_MAX_PAUSE_BUFFER_SEC &&
                queuedAudioBuffers.length > 0
            ) {
                const removed = queuedAudioBuffers.shift();
                if (removed) queuedAudioDurationSec -= removed.duration;
            }
        }
    };

    const flushPendingBuffers = () => {
        if (queuedAudioBuffers.length === 0) return;
        const buffers = [...queuedAudioBuffers];
        queuedAudioBuffers.length = 0;
        queuedAudioDurationSec = 0;
        buffers.forEach((buf) => scheduleAudioBuffer(buf));
    };

    // Cleanup streaming resources
    function cleanupStreamingAudio(immediateStop: boolean) {
        isStreamActive = false;
        hasStreamCompleted = false;

        // Cancel any active stream reader
        sseHandler.abort();

        // Stop all scheduled nodes
        if (immediateStop) {
            scheduledSourceNodes.forEach((node) => {
                try {
                    node.stop();
                } catch (error) {
                    // Node may already be stopped or in invalid state
                    console.debug('AudioNode stop failed during cleanup:', error);
                }
                try {
                    node.disconnect();
                } catch (error) {
                    console.debug('AudioNode disconnect failed during cleanup:', error);
                }
            });
            scheduledSourceNodes.clear();
        }

        // Reset scheduling
        nextPlaybackStartTime = 0;
        set({ hasActive: false, isPaused: false, isLoading: false }, false, 'cleanupStreamingAudio');
        paused = false;
        
        // Clear any pending buffers
        queuedAudioBuffers.length = 0;
        queuedAudioDurationSec = 0;

        if (immediateStop) {
            if (audioContext) {
                try {
                    closeSharedAudioContext();
                } catch (error) {
                    console.debug('AudioContext close failed during cleanup:', error);
                }
            }
            audioContext = null;
        }
    }
    
    // Decode each chunk and either schedule for playback or buffer if paused
    const handleChunk = async (chunk: TTSStreamChunk) => {
        if (!isStreamActive) return;
        try {
            const ctx = getOrCreateAudioContext();
            const arrayBuffer = base64ToArrayBuffer(chunk.audio_data);
            const audioBuffer = await ctx.decodeAudioData(
                arrayBuffer.slice(0),
            );
            
            // Add to cache
            cachedAudioBuffers.push(audioBuffer);
        
            if (paused) {
                enqueueBuffer(audioBuffer);
            } else {
                scheduleAudioBuffer(audioBuffer);
            }
            if (!hasReceivedFirstChunk && isStreamActive) {
                hasReceivedFirstChunk = true;
                set(
                    { isLoading: false, isPlaying: true, hasActive: true },
                    false,
                    'tts/streamFirstChunk',
                );
            }
        } catch (error) {
            console.error('Failed to process TTS chunk', error);
        }
    };

    const handleDone = (streamData?: { done?: boolean; aborted?: boolean }) => {
        // Final chunk/done received from main process
        // If paused, keep buffers and controls so user can resume to finish playback
        hasStreamCompleted = true;
        set({ isLoading: false }, false, 'tts/streamDone');

        // Mark cache as complete if not aborted
        if (!streamData?.aborted) {
            hasCompleteAudioCached = true;
            const totalDuration = cachedAudioBuffers.reduce(
                (total, buffer) => total + buffer.duration, 0
            );
            console.log(`[TTS Cache] Cache complete: ${cachedAudioBuffers.length} chunks, duration: ${totalDuration.toFixed(2)}s`);
        } else {
            // Clear cache on abort
            cachedAudioBuffers.length = 0;
            hasCompleteAudioCached = false;
        }

        // Explicit abort: stop immediately and clear everything
        if (streamData?.aborted) {
            cleanupStreamingAudio(true);
            set({ isPlaying: false, hasActive: false }, false, 'tts/streamAborted');
            return;
        }

        if (paused) {
            return;
        }
        
        setTimeout(() => {
            // If nothing is scheduled and not paused, finalize now
            if (scheduledSourceNodes.size === 0 && !paused) {
                cleanupStreamingAudio(false);
                set(
                    { isPlaying: false, hasActive: false },
                    false,
                    'tts/streamPlaybackComplete',
                );
            }
        }, 500);
    };

    const handleError = (
        errorDetails: { error: string; details?: string },
    ) => {
        console.error('TTS stream error:', errorDetails);
        useToastStore.getState().showToast(errorDetails?.error || 'TTS stream error', 'error', 3000);
        cleanupStreamingAudio(true);
        set({ isPlaying: false, isLoading: false }, false, 'tts/streamError');
    };


    const playAPI = async (message: MessageModel, sanitize?: boolean, language?: string) => {
        // Check if this is a new message different from our cached one
        if (message.content !== cachedAudioMessage) {
            console.log(`[TTS Cache] New message differs from cache, clearing cache`);
            // Clear existing cache since we're processing new text
            cachedAudioBuffers.length = 0;
            hasCompleteAudioCached = false;
            
            // Store the new message content for cache identification
            cachedAudioMessage = message.content;
        }
        
        hasReceivedFirstChunk = false;
        set(
            { isLoading: true, isPaused: false, hasActive: true },
            false,
            'tts/playStart',
        );
        paused = false;

        try {
            if (!message?.content) return;
            const ctx = getOrCreateAudioContext();
            nextPlaybackStartTime = ctx.currentTime;
            scheduledSourceNodes.clear();
            isStreamActive = true;
            hasStreamCompleted = false;
            set({ hasActive: true, isLoading: true }, false, 'tts/streamStart');

            // Start streaming
            const response = apiClient.audio.textToSpeechStream(
                message.content,
                undefined, // Use default voice
                language
            );

            await sseHandler.processResponse(
                response,
                new AbortController(),
                async (data) => {
                    await handleChunk(data as TTSStreamChunk);
                },
                () => {
                    handleDone({ done: true });
                },
                (err) => {
                    if (err.name === 'AbortError') return;
                    handleError({ error: err.message || 'TTS streaming failed' });
                }
            );

        } catch (err: any) {
            if (err.name === 'AbortError') return;
            handleError({ error: err.message || 'TTS streaming failed' });
        }
    };

    return {
        // Initial State
        isVisible: false,
        text: '',
        language: 'en-us',
        source: null,
        isInTransition: false,
        isPlaying: false,
        isLoading: false,
        isPaused: false,
        hasActive: false,

        // Actions
        showTTSPlayer: (newText: string, newLanguage?: string, newSource?: 'response' | 'text-selection') => {
            set((state) => ({
                text: newText,
                language: newLanguage || state.language,
                source: newSource || state.source,
                isVisible: true
            }), false, 'tts/showPlayer');
        },

        hideTTSPlayer: () => {
            set({ isVisible: false }, false, 'tts/hidePlayer');
            sessionStorage.removeItem('tts-was-active');
        },

        play: async (message: MessageModel, sanitize?: boolean, language?: string, source: 'response' | 'text-selection' = 'response') => {
            const state = get();
            
            if (state.hasActive) {
                // Transition logic
                set({ isInTransition: true }, false, 'tts/transitionStart');

                // Stop current playback
                get().stop(true);
                set({ source }, false, 'tts/setSource');
                get().showTTSPlayer(message.content, language);

                sessionStorage.setItem('tts-was-active', 'true');

                // Wait before starting new playback (as per original context logic)
                setTimeout(async () => {
                    if (get().isInTransition) {
                        try {
                            await playAPI(message, sanitize, language);
                        } catch (error) {
                            console.error("Failed to start TTS playback:", error);
                            useToastStore.getState().showToast("Failed to start audio playback", "error", 3000);
                        } finally {
                            set({ isInTransition: false }, false, 'tts/transitionEnd');
                        }
                    }
                }, 8000);
            } else {
                // Regular play
                set({ source }, false, 'tts/setSource');
                get().showTTSPlayer(message.content, language);
            
                // Store that TTS was active for potential session recovery
                sessionStorage.setItem('tts-was-active', 'true');
                
                try {
                    await playAPI(message, sanitize, language);
                } catch (error) {
                    console.error("Failed to start TTS playback:", error);
                    useToastStore.getState().showToast("Failed to start audio playback", "error", 3000);
                }
            }
        },

        stop: (clearCache = true) => {
            apiClient.audio.stopTTSStream().catch((err: any) => {
                console.debug('Error aborting TTS stream:', err);
            });
            set({ isLoading: false }, false, 'tts/stop');
            
            // Stop streaming immediately
            if (
                isStreamActive ||
                sseHandler.isActive() ||
                scheduledSourceNodes.size > 0
            ) {
                cleanupStreamingAudio(true);
            }
            
            if (clearCache) {
                console.log(`[TTS Cache] Clearing ${cachedAudioBuffers.length} chunks from cache`);
                cachedAudioBuffers.length = 0;
                hasCompleteAudioCached = false;
                cachedAudioMessage = '';
            }
            
            set(
                { isPlaying: false, isPaused: false, hasActive: false },
                false,
                'tts/stopped',
            );
            paused = false;
            get().hideTTSPlayer();
        },

        pause: async () => {
            const state = get();
            if (audioContext && (isStreamActive || state.hasActive)) {
                try {
                    await suspendSharedAudioContext();
                } catch (error) {
                    console.warn('Failed to pause audio context:', error);
                }
                set(
                    { isPlaying: false, isPaused: true, hasActive: true },
                    false,
                    'tts/pause',
                );
                paused = true;
            }
        },

        resume: async () => {
            const state = get();
            if (audioContext && (isStreamActive || state.hasActive)) {
                try {
                    await resumeSharedAudioContext();
                } catch (error) {
                    console.warn('Failed to resume audio context:', error);
                }
                set(
                    { isPlaying: true, isPaused: false, hasActive: true },
                    false,
                    'tts/resume',
                );
                paused = false;
                flushPendingBuffers();
            }
        },

        replay: async () => {
            if (!hasCompleteAudioCached || cachedAudioBuffers.length === 0) {
                console.log(`[TTS Cache] Replay failed: No complete cache available`);
                return false;
            }
            
            console.log(`[TTS Cache] Replaying from cache: ${cachedAudioBuffers.length} chunks`);
            
            set(
                { isLoading: false, isPaused: false, hasActive: true },
                false,
                'tts/replayStart',
            );
            paused = false;
            
            try {
                const ctx = getOrCreateAudioContext();
                nextPlaybackStartTime = ctx.currentTime;
                scheduledSourceNodes.clear();
                
                // Mark stream as completed so that onended handlers will clean up properly
                hasStreamCompleted = true;
                
                // Play all cached buffers
                cachedAudioBuffers.forEach((buffer) => {
                    scheduleAudioBuffer(buffer);
                });
                
                set({ isPlaying: true }, false, 'tts/replayPlaying');
                
                // Add a final check in case onended handlers don't fire properly
                const totalDuration = cachedAudioBuffers.reduce(
                    (total, buffer) => total + buffer.duration, 0
                );
                console.log(`[TTS Cache] Scheduled ${cachedAudioBuffers.length} cached chunks, duration: ${totalDuration.toFixed(2)}s`);
                
                // Add a safety timeout that's slightly longer than the audio duration
                const safetyTimeoutMs = (totalDuration * 1000) + 500;
                setTimeout(() => {
                    // Only cleanup if still marked as playing (hasn't been stopped manually)
                    if (hasStreamCompleted && scheduledSourceNodes.size === 0) {
                        cleanupStreamingAudio(false);
                        set(
                            { isPlaying: false, hasActive: false },
                            false,
                            'tts/replayComplete',
                        );
                    }
                }, safetyTimeoutMs);
                
                return true;
            } catch (err) {
                console.error('Failed to replay cached TTS:', err);
                set(
                    { isPlaying: false, hasActive: false },
                    false,
                    'tts/replayFailed',
                );
                return false;
            }
        },

        hasCachedAudio: () => hasCompleteAudioCached && cachedAudioBuffers.length > 0,
        getCachedMessage: () => cachedAudioMessage,
        isActiveSource: (checkSource: 'response' | 'text-selection') => {
            return get().source === checkSource && get().hasActive;
        }
    };
        },
        { name: 'ttsStore' },
    ),
);

// Global listener for stop-tts event
if (typeof window !== 'undefined') {
    window.addEventListener('stop-tts', () => {
        const state = useTTSStore.getState();
        if (state.hasActive) {
            state.stop();
        }
    });
}
